import { useEffect, useState } from 'react'
import {
  SLOW_LOADING_AFTER_MS,
  loadingMessage,
  type LoadingSteps,
} from '../../loadingMessage'

/**
 * LoadingState — the ONE loading indicator for the Control Center and Admin.
 *
 * Shows a plain label ("Loading your schedule…") with a spinning ring, so a screen never
 * sits blank or as gray boxes with no words (John, 2026-10-14). Rules live in
 * `loadingMessage.ts`:
 *   - say what is loading; count real steps when there are several ("1 of 3 ready");
 *   - never a fake percentage for a single request;
 *   - after ~8 seconds the words change to "Still loading…" so it never looks frozen.
 *
 * Accessibility: role="status" + aria-live="polite" so screen readers hear the label.
 * With reduced motion turned on, the ring stops spinning but stays visible (motion-safe).
 */

export type LoadingVariant = 'page' | 'card' | 'inline'

export interface LoadingStateProps {
  /** Plain words: "Loading your customers…". Defaults to "Loading…". */
  label?: string
  /** Several independent loads → show how many are done. */
  steps?: LoadingSteps
  /** page = centred in a tall area · card = inside a card · inline = one line. */
  variant?: LoadingVariant
  /** Override when the "still loading" wording appears (tests / unusual screens). */
  slowAfterMs?: number
  className?: string
}

function useSlowAfter(ms: number): boolean {
  const [slow, setSlow] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), ms)
    return () => clearTimeout(t)
  }, [ms])
  return slow
}

/** The spinning ring. Static (still visible) when the viewer prefers reduced motion. */
export function LoadingSpinner({ size = 'md' }: { size?: 'sm' | 'md' }) {
  const dims = size === 'sm' ? 'w-4 h-4 border-2' : 'w-8 h-8 border-[3px]'
  return (
    <span
      aria-hidden="true"
      data-loading-spinner
      className={`inline-block shrink-0 rounded-full border-teal-600 border-t-transparent dark:border-teal-400 dark:border-t-transparent motion-safe:animate-spin motion-reduce:border-dotted ${dims}`}
    />
  )
}

export function LoadingState({
  label,
  steps,
  variant = 'page',
  slowAfterMs = SLOW_LOADING_AFTER_MS,
  className = '',
}: LoadingStateProps) {
  const slow = useSlowAfter(slowAfterMs)
  const text = loadingMessage({ label, steps, slow })

  if (variant === 'inline') {
    return (
      <div role="status" aria-live="polite" data-loading-state className={`flex items-center gap-2 py-2 ${className}`}>
        <LoadingSpinner size="sm" />
        <span className="text-sm theme-text-secondary">{text}</span>
      </div>
    )
  }

  const outer = variant === 'card'
    ? `card p-6 flex flex-col items-center justify-center gap-3 text-center ${className}`
    : `flex flex-col items-center justify-center gap-3 text-center min-h-[16rem] px-4 ${className}`

  return (
    <div role="status" aria-live="polite" data-loading-state className={outer}>
      <LoadingSpinner />
      <p className="text-sm theme-text-secondary">{text}</p>
    </div>
  )
}

export interface LoadErrorProps {
  /** Plain words: "I couldn’t load your schedule just now." */
  message?: string
  /** Optional second line (e.g. "Text me … and I’ll list it for you."). */
  detail?: string
  onRetry?: () => void
  variant?: LoadingVariant
  className?: string
}

/** What a screen shows when its data did not load — never a blank. */
export function LoadError({
  message = 'That didn’t load just now.',
  detail,
  onRetry,
  variant = 'card',
  className = '',
}: LoadErrorProps) {
  const outer = variant === 'card'
    ? `card p-6 flex flex-col items-center justify-center gap-3 text-center ${className}`
    : variant === 'inline'
      ? `flex flex-wrap items-center gap-3 py-2 ${className}`
      : `flex flex-col items-center justify-center gap-3 text-center min-h-[16rem] px-4 ${className}`
  return (
    <div role="alert" data-load-error className={outer}>
      <div>
        <p className="text-sm theme-text">{message}</p>
        {detail && <p className="text-xs theme-text-muted mt-1">{detail}</p>}
      </div>
      {onRetry && (
        <button type="button" onClick={onRetry} className="btn-secondary text-sm px-3 py-1.5 rounded-lg">
          Try again
        </button>
      )}
    </div>
  )
}
